<template>
  <USlideover v-model:open="isOpen" title="Coșul tău" side="right">
    <template #body>
      <div v-if="items.length === 0" class="flex flex-col items-center justify-center h-full text-center py-12">
        <UIcon name="i-heroicons-shopping-cart" class="w-16 h-16 text-dimmed mb-4" />
        <p class="text-muted mb-4">Coșul tău este gol</p>
        <UButton label="Continuă cumpărăturile" :to="shopPath('/produse')" @click="closeDrawer" />
      </div>

      <div v-else class="flex flex-col h-full">
        <div class="flex-1 overflow-y-auto space-y-4">
          <div
            v-for="item in items"
            :key="`${item.productId}-${item.variantId || 0}-${item.customFieldsKey || ''}`"
            class="flex items-start gap-3 pb-4 border-b border-muted"
          >
            <AppImage
              :src="item.product?.images?.find(i => i.isPrimary)?.url || item.product?.images?.[0]?.url"
              :alt="item.product?.name"
              preset="thumbnail"
              width="80"
              height="80"
              sizes="80px"
              class="w-20 h-20 shrink-0 object-cover rounded-lg bg-muted"
              loading="lazy"
            />
            <div class="flex-1 min-w-0">
              <NuxtLink
                :to="shopPath(`/produse/${item.product?.slug}`)"
                class="font-medium text-sm text-highlighted line-clamp-2 hover:text-primary-600"
                @click="closeDrawer"
              >
                {{ item.product?.name }}
              </NuxtLink>
              <p v-if="item.variant" class="text-xs text-muted mt-0.5">
                {{ item.variant.color }} {{ item.variant.storage }}
              </p>
              <CartPriceBreakdown
                v-if="item.customFieldsSnapshot?.length"
                :unit-price="item.unitPrice"
                :fields="item.customFieldsSnapshot"
                hide-customer-hidden-fields
              />
              <div class="flex items-center justify-between mt-2 gap-2">
                <UInputNumber
                  :model-value="item.quantity"
                  :min="linePriceUnit(item) ? 0.01 : 1"
                  :step="linePriceUnit(item) ? 0.01 : 1"
                  :max="!stockManagementEnabled
                    || shopServicesEnabled
                    || item.product?.generatorId
                    || item.product?.productType === 'package'
                    || item.packageItemProductIds?.length
                    ? undefined
                    : (item.variant?.stock ?? item.product?.stock ?? 1)"
                  size="xs"
                  class="w-24"
                  @update:model-value="(val: number) => updateQuantity(item.productId, val, item.variantId, item.customFieldsKey)"
                />
                <span class="font-semibold text-sm shrink-0">{{ formatPrice(item.totalPrice) }}</span>
              </div>
            </div>
            <button
              type="button"
              class="shrink-0 flex h-8 w-8 items-center justify-center rounded-lg text-dimmed transition-colors hover:bg-rose-50 dark:hover:bg-rose-950/40 hover:text-rose-600"
              aria-label="Elimină din coș"
              @click="removeFromCart(item.productId, item.variantId, item.customFieldsKey)"
            >
              <UIcon name="i-heroicons-trash" class="h-4 w-4" />
            </button>
          </div>
        </div>

        <div class="border-t border-default pt-4 mt-4 space-y-3">
          <FreeShippingBar v-if="!shopServicesEnabled && !tariffShippingEnabled" :subtotal="subtotal" />
          <div class="flex justify-between text-lg font-semibold">
            <span>Subtotal produse</span>
            <span>{{ formatPrice(subtotal) }}</span>
          </div>
          <UButton label="Vezi coșul" :to="shopPath('/cos')" block variant="solid" @click="closeDrawer" />
          <UButton label="Finalizează comanda" :to="shopPath('/checkout')" block color="primary" @click="closeDrawer" />
        </div>
      </div>
    </template>
  </USlideover>
</template>

<script setup lang="ts">
import { linePriceUnit } from '~/utils/shopServices'

const shopPath = useShopPath()
const { items, subtotal, isOpen, closeDrawer, updateQuantity, removeFromCart } = useCart()
const { stockManagementEnabled, shopServicesEnabled, shippingMethods } = useSiteSettings()
const { formatPrice } = useSeo()
const tariffShippingEnabled = computed(() =>
  shippingMethods.value.transport_intervals || shippingMethods.value.transport_procentual,
)
</script>
